import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Divider,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  Stack,
  TextField,
} from '@mui/material'
import AdminMainContainer from '../../../../layouts/admin/AdminMainContainer'
import useAdminMedicalReimbursementEditViewModel from './viewModel'
import { Controller } from 'react-hook-form'
import { ReimbursementType } from '../../../../database/enums/ReimbursementType.enum'
import ReimbursementTypeEnumHelper from '../../../../database/enums/helpers/reimbursement-type.helper'
import { DatePicker } from '@mui/x-date-pickers'
import CurrencyEnumHelper from '../../../../database/enums/helpers/currency.helper'
import { Currency } from '../../../../database/enums/Currency.enum'
import IconSave from '@mui/icons-material/Save'

const scopeNamePrefix = 'admin-medical-reimbursement-edit'

export default function AdminMedicalReimbursementEditPage() {
  const viewModel = useAdminMedicalReimbursementEditViewModel()

  const renderLoadingState = () => {
    return (
      <Box sx={{ width: '100%', maxWidth: '500px' }}>
        <Card elevation={5}>
          <CardContent>
            <Stack gap={2}>
              <Skeleton variant='text' />
              <Skeleton variant='rounded' height={48} />
              <Skeleton variant='rectangular' height={96} />
            </Stack>
          </CardContent>
        </Card>
      </Box>
    )
  }

  const renderDetailState = () => {
    if (!!viewModel.detailData)
      return (
        <Box sx={{ width: '100%', maxWidth: '500px' }}>
          <form onSubmit={viewModel.formHook.handleSubmit(viewModel.onSubmit)}>
            <Card elevation={5}>
              <CardContent>
                <Stack gap={2}>
                  <Controller
                    control={viewModel.formHook.control}
                    name='name'
                    render={({ field, fieldState }) => (
                      <TextField
                        variant='outlined'
                        required
                        label='Name'
                        placeholder='Enter your name...'
                        {...field}
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                      />
                    )}
                  />
                  <Controller
                    control={viewModel.formHook.control}
                    name='email'
                    render={({ field, fieldState }) => (
                      <TextField
                        variant='outlined'
                        required
                        label='Email'
                        placeholder='Enter your email...'
                        type='email'
                        {...field}
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                      />
                    )}
                  />
                </Stack>
              </CardContent>
              <Divider />
              <CardContent>
                <Stack gap={2}>
                  <Controller
                    control={viewModel.formHook.control}
                    name='reimbursementType'
                    render={({ field }) => (
                      <FormControl fullWidth required>
                        <InputLabel id={`${scopeNamePrefix}-reimbursementType-label`}>Reimbursement Type</InputLabel>
                        <Select
                          labelId={`${scopeNamePrefix}-reimbursementType-label`}
                          label='Type of reimbursement'
                          {...field}
                        >
                          {Object.values(ReimbursementType).map((item, index) => (
                            <MenuItem key={`${scopeNamePrefix}-reimbursementType-option-${index}`} value={item}>
                              {ReimbursementTypeEnumHelper.getLabel(item)}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  />
                  <Controller
                    control={viewModel.formHook.control}
                    name='serviceDate'
                    render={({ field, fieldState }) => (
                      <DatePicker
                        label='Date of Service'
                        {...field}
                        slotProps={{
                          textField: {
                            required: true,
                            error: !!fieldState.error,
                            helperText: fieldState.error?.message,
                          },
                        }}
                        sx={{ flex: 1 }}
                      />
                    )}
                  />
                  <Controller
                    control={viewModel.formHook.control}
                    name='vendor'
                    render={({ field, fieldState }) => (
                      <TextField
                        variant='outlined'
                        required
                        label='Vendor'
                        placeholder='Enter the vendor name...'
                        {...field}
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                      />
                    )}
                  />
                  <Controller
                    control={viewModel.formHook.control}
                    name='description'
                    render={({ field }) => (
                      <TextField
                        variant='outlined'
                        label='Description'
                        placeholder='Enter the description...'
                        multiline
                        minRows={3}
                        {...field}
                      />
                    )}
                  />
                </Stack>
              </CardContent>
              <Divider />
              <CardContent>
                <Stack gap={2}>
                  <Controller
                    control={viewModel.formHook.control}
                    name='totalAmountClaimed.value'
                    render={({ field, fieldState }) => (
                      <TextField
                        variant='outlined'
                        required
                        label='Total Amount Claimed'
                        placeholder='Enter the amount...'
                        slotProps={{
                          input: {
                            startAdornment: (
                              <Controller
                                control={viewModel.formHook.control}
                                name='totalAmountClaimed.currency'
                                render={totalAmountClaimedCurrencyForm => (
                                  <InputAdornment position='start'>
                                    {CurrencyEnumHelper.getSymbol(
                                      totalAmountClaimedCurrencyForm.field.value as Currency,
                                    )}
                                  </InputAdornment>
                                )}
                              />
                            ),
                          },
                        }}
                        {...field}
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                      />
                    )}
                  />
                </Stack>
              </CardContent>
              <Divider />
              <CardActions sx={{ justifyContent: 'end' }}>
                <Button onClick={viewModel.resetPage} variant='text'>
                  Reset form
                </Button>
                <Button
                  type='submit'
                  variant='contained'
                  startIcon={<IconSave />}
                  disabled={viewModel.dataUpdateStatus.isLoading}
                  loading={viewModel.dataUpdateStatus.isLoading}
                >
                  Save
                </Button>
              </CardActions>
            </Card>
          </form>
        </Box>
      )
  }

  const renderErrorState = () => {
    return (
      <Alert severity='error' variant='filled'>
        <AlertTitle>Error!</AlertTitle>
        Document not found!
      </Alert>
    )
  }

  return (
    <AdminMainContainer title='Medical Reimbursement Edit' backButton={{ show: true }}>
      {viewModel.dataFetchStatus.isLoading
        ? renderLoadingState()
        : viewModel.dataFetchStatus.isSuccess && !!viewModel.detailData
          ? renderDetailState()
          : viewModel.dataFetchStatus.isError
            ? renderErrorState()
            : null}
    </AdminMainContainer>
  )
}
